import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ArrowDown, ArrowUp, Plus, RefreshCw, RotateCcw, Save, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UnifiedTable } from '@/components/ui/unifiedTable/UnifiedTable';
import type { UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';

type RoutingAction = 'discard' | 'forward' | 'support';
type RoutingRule = { keywords: string[]; action: RoutingAction; recipients: string[] };
type RoutingSettings = { recipients: string[]; rules: RoutingRule[]; legacyFormat: boolean };
type RoutingQuery = { getSupportMailRouting: RoutingSettings };
type RoutingMutation = { updateSupportMailRouting: RoutingSettings };
type RuleDraft = { keywords: string; action: RoutingAction; recipients: string };
type RoutingForm = { recipients: string; rules: RuleDraft[] };
type RuleRow = RuleDraft & { id: string; index: number };

const GET_ROUTING = gql`
  query AdminSupportMailRouting {
    getSupportMailRouting {
      recipients
      legacyFormat
      rules { keywords action recipients }
    }
  }
`;

const SAVE_ROUTING = gql`
  mutation AdminUpdateSupportMailRouting($input: SupportMailRoutingSettingsInput!) {
    updateSupportMailRouting(input: $input) {
      recipients
      legacyFormat
      rules { keywords action recipients }
    }
  }
`;

const ACTIONS: Array<{ value: RoutingAction; label: string }> = [
  { value: 'discard', label: 'Discard' },
  { value: 'forward', label: 'Forward to recipients' },
  { value: 'support', label: 'Support queue' },
];

const emailList = (value: string): string[] => value.split(/[\s,;]+/).map(address => address.trim()).filter(Boolean);
const keywordList = (value: string): string[] => value.split(/\r?\n/).map(keyword => keyword.trim()).filter(Boolean);
const validateRecipients = (value: string): true | string => {
  const addresses = emailList(value);
  if (!addresses.length) return 'Enter at least one recipient address.';
  if (addresses.some(address => !/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i.test(address))) {
    return 'Enter valid email addresses separated by commas or new lines.';
  }
  return true;
};

const toForm = (settings: RoutingSettings): RoutingForm => ({
  recipients: settings.recipients.join('\n'),
  rules: settings.rules.map(rule => ({
    keywords: rule.keywords.join('\n'), action: rule.action, recipients: rule.recipients.join('\n'),
  })),
});

const RoutingEditor: React.FC<{
  settings: RoutingSettings;
  reloading: boolean;
  loadError?: string;
  onReload: () => Promise<unknown>;
}> = ({ settings, reloading, loadError, onReload }) => {
  const [notice, setNotice] = useState<{ error: boolean; message: string } | null>(null);
  const {
    control, register, watch, reset, handleSubmit, setValue, clearErrors, getValues,
    formState: { errors, isDirty },
  } = useForm<RoutingForm>({ defaultValues: toForm(settings), mode: 'onBlur' });
  const dirty = useRef(isDirty);
  dirty.current = isDirty;
  const { fields, append, remove, move } = useFieldArray({ control, name: 'rules' });
  const draftRules = watch('rules');
  const [saveRouting, { loading: saving }] = useMutation<RoutingMutation>(SAVE_ROUTING, {
    update: (cache, response) => {
      const saved = response.data?.updateSupportMailRouting;
      if (!saved) return;
      cache.writeQuery<RoutingQuery>({ query: GET_ROUTING, data: { getSupportMailRouting: saved } });
    },
  });
  const busy = saving || reloading;

  useEffect(() => {
    if (dirty.current) return;
    reset(toForm(settings));
  }, [settings, reset]);

  const save = async (values: RoutingForm) => {
    setNotice(null);
    try {
      const response = await saveRouting({ variables: { input: {
        recipients: emailList(values.recipients),
        rules: values.rules.map(rule => ({
          keywords: keywordList(rule.keywords), action: rule.action,
          ...(rule.action === 'forward' ? { recipients: emailList(rule.recipients) } : {}),
        })),
      } } });
      const saved = response.data?.updateSupportMailRouting;
      if (!saved) throw new Error('The server did not return saved routing settings.');
      reset(toForm(saved));
      setNotice({ error: false, message: 'Inbound mail routing saved. Changes apply to the next processing batch.' });
    } catch (error) {
      setNotice({ error: true, message: error instanceof Error ? error.message : 'Unable to save inbound mail routing.' });
    }
  };

  const reload = async () => {
    setNotice(null);
    try {
      await onReload();
    } catch {
      setNotice({ error: true, message: 'Unable to reload inbound mail routing.' });
    }
  };

  const rows: RuleRow[] = fields.map((field, index) => ({ ...field, ...draftRules[index], index }));
  const columns: UnifiedTableColumn<RuleRow>[] = [
    { key: 'order', header: 'Order', width: '60px', accessor: row => row.index + 1 },
    { key: 'keywords', header: 'Message keywords', width: '35%', accessor: row => (
      <div className="space-y-1">
        <Textarea
          id={`inbound-routing-rule-${row.id}-keywords`}
          aria-label={`Rule ${row.index + 1} message keywords`}
          aria-invalid={Boolean(errors.rules?.[row.index]?.keywords)}
          aria-describedby={`inbound-routing-rule-${row.id}-keywords-error`}
          rows={2}
          className="min-h-[64px]"
          placeholder="One keyword or phrase per line"
          disabled={busy}
          {...register(`rules.${row.index}.keywords`, {
            validate: value => keywordList(value).length > 0 || 'Enter at least one keyword or phrase.',
          })}
        />
        <p id={`inbound-routing-rule-${row.id}-keywords-error`} className="text-sm text-[var(--semantic-error)]">
          {errors.rules?.[row.index]?.keywords?.message}
        </p>
      </div>
    ) },
    { key: 'action', header: 'Action', width: '180px', accessor: row => (
      <Controller
        control={control}
        name={`rules.${row.index}.action`}
        render={({ field }) => (
          <Select value={field.value} disabled={busy} onValueChange={value => {
            if (!ACTIONS.some(action => action.value === value)) return;
            field.onChange(value);
            if (value === 'forward') return;
            setValue(`rules.${row.index}.recipients`, '', { shouldDirty: true });
            clearErrors(`rules.${row.index}.recipients`);
          }}>
            <SelectTrigger
              id={`inbound-routing-rule-${row.id}-action`}
              aria-label={`Rule ${row.index + 1} action`}
              ref={field.ref}
              onBlur={field.onBlur}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIONS.map(action => <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      />
    ) },
    { key: 'recipients', header: 'Recipients', width: '30%', accessor: row => {
      if (row.action !== 'forward') {
        return <span className="text-sm text-muted-foreground">{row.action === 'support' ? 'Support staff below' : 'No delivery'}</span>;
      }
      return (
        <div className="space-y-1">
          <Textarea
            id={`inbound-routing-rule-${row.id}-recipients`}
            aria-label={`Rule ${row.index + 1} forwarding recipients`}
            aria-invalid={Boolean(errors.rules?.[row.index]?.recipients)}
            aria-describedby={`inbound-routing-rule-${row.id}-recipients-error`}
            rows={2}
            className="min-h-[64px]"
            placeholder="recipient@example.org"
            disabled={busy}
            {...register(`rules.${row.index}.recipients`, {
              validate: value => getValues(`rules.${row.index}.action`) !== 'forward' || validateRecipients(value),
            })}
          />
          <p id={`inbound-routing-rule-${row.id}-recipients-error`} className="text-sm text-[var(--semantic-error)]">
            {errors.rules?.[row.index]?.recipients?.message}
          </p>
        </div>
      );
    } },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 p-4">
        <div>
          <CardTitle>Inbound Mail Routing</CardTitle>
          <CardDescription className="mt-1">Choose who receives incoming mail and which messages to discard or route.</CardDescription>
        </div>
        <Button id="inbound-routing-reload" type="button" variant="outline" disabled={busy || isDirty} onClick={reload}>
          <RefreshCw className="h-4 w-4" /> Reload
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        {loadError && <Alert variant="destructive"><AlertDescription>{loadError}</AlertDescription></Alert>}
        {notice && (
          <Alert variant={notice.error ? 'destructive' : 'default'} role={notice.error ? 'alert' : 'status'}>
            <AlertDescription>{notice.message}</AlertDescription>
          </Alert>
        )}
        {settings.legacyFormat && (
          <Alert><AlertDescription>
            Mail currently uses the existing recipient-based handling. Saving here makes the support queue the default for unmatched mail.
          </AlertDescription></Alert>
        )}
        <form id="inbound-routing-form" onSubmit={handleSubmit(save)} noValidate className="space-y-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold">Message rules</h3>
                <p className="text-sm text-muted-foreground">First matching rule wins. Match any keyword in the subject or body, ignoring case.</p>
              </div>
              <Button id="inbound-routing-add-rule" type="button" variant="outline" disabled={busy} onClick={() => {
                append({ keywords: '', action: 'support', recipients: '' });
              }}>
                <Plus className="h-4 w-4" /> Add rule
              </Button>
            </div>
            <UnifiedTable
              columns={columns}
              data={rows}
              rowKey={row => row.id}
              clientSort={false}
              showRowActions
              density="compact"
              emptyTitle="No message rules"
              emptyDescription="After saving, all unmatched incoming mail goes to the support queue."
              renderRowActions={row => (
                <div className="flex items-center gap-1">
                  <Button
                    id={`inbound-routing-rule-${row.id}-up`}
                    type="button" variant="outline" className="w-8 p-0"
                    aria-label={`Move rule ${row.index + 1} up`}
                    disabled={busy || row.index === 0} onClick={() => move(row.index, row.index - 1)}
                  ><ArrowUp className="h-4 w-4" /></Button>
                  <Button
                    id={`inbound-routing-rule-${row.id}-down`}
                    type="button" variant="outline" className="w-8 p-0"
                    aria-label={`Move rule ${row.index + 1} down`}
                    disabled={busy || row.index === fields.length - 1} onClick={() => move(row.index, row.index + 1)}
                  ><ArrowDown className="h-4 w-4" /></Button>
                  <Button
                    id={`inbound-routing-rule-${row.id}-remove`}
                    type="button" variant="ghost" className="w-8 p-0 text-[var(--semantic-error)]"
                    aria-label={`Remove rule ${row.index + 1}`}
                    disabled={busy} onClick={() => remove(row.index)}
                  ><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            />
          </div>
          <div className="max-w-2xl space-y-2">
            <Label htmlFor="inbound-routing-staff">Support staff recipients</Label>
            <p className="text-sm text-muted-foreground">Unmatched mail and support rules create a support ticket and notify everyone listed here.</p>
            <Textarea
              id="inbound-routing-staff"
              rows={3}
              placeholder="staff@example.org"
              disabled={busy}
              aria-invalid={Boolean(errors.recipients)}
              aria-describedby="inbound-routing-staff-error"
              {...register('recipients', { validate: validateRecipients })}
            />
            <p className="text-sm text-muted-foreground">Separate email addresses with commas or new lines.</p>
            <p id="inbound-routing-staff-error" className="text-sm text-[var(--semantic-error)]">{errors.recipients?.message}</p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--neutral-gray-30)] pt-4">
            <p className="text-sm text-muted-foreground">{isDirty ? 'Unsaved changes' : 'No pending changes'}</p>
            <div className="flex items-center gap-2">
              <Button id="inbound-routing-reset" type="button" variant="outline" disabled={busy || !isDirty} onClick={() => {
                reset(toForm(settings));
                setNotice(null);
              }}><RotateCcw className="h-4 w-4" /> Reset changes</Button>
              <Button id="inbound-routing-save" type="submit" disabled={busy || !isDirty}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save routing'}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export const InboundMailRoutingPage: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<RoutingQuery>(GET_ROUTING, { fetchPolicy: 'network-only' });
  if (!data?.getSupportMailRouting) {
    return (
      <Card>
        <CardHeader className="p-4"><CardTitle>Inbound Mail Routing</CardTitle></CardHeader>
        <CardContent className="space-y-3 p-4 pt-0">
          {loading && <p role="status" className="text-sm text-muted-foreground">Loading routing settings…</p>}
          {!loading && (
            <>
              <Alert variant="destructive"><AlertDescription>{error?.message || 'Unable to load inbound mail routing.'}</AlertDescription></Alert>
              <Button id="inbound-routing-retry" type="button" variant="outline" onClick={() => { void refetch().catch(() => undefined); }}>
                <RefreshCw className="h-4 w-4" /> Retry
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }
  return <RoutingEditor settings={data.getSupportMailRouting} reloading={loading} loadError={error?.message} onReload={refetch} />;
};
